import { MONITORING_FEED_COPY } from '#app/routes/mocra+/_form+/components/monitoring-feeds/constants.ts'
import { type MonitoringFeedEntry } from '#app/routes/mocra+/_form+/components/monitoring-feeds/schema.ts'
import {
	REGULATORY_FEED_FREQUENCY_OPTIONS,
	REGULATORY_FEED_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

export type MonitoringFeedSourceValue =
	(typeof REGULATORY_FEED_OPTIONS)[number]['value']

export type MonitoringFeedFrequencyValue =
	(typeof REGULATORY_FEED_FREQUENCY_OPTIONS)[number]['value']

export type MonitoringFeedDraft = {
	source: MonitoringFeedSourceValue
	frequency: MonitoringFeedFrequencyValue
	detail: string
}

export function createMonitoringFeedDraft(
	overrides: Partial<MonitoringFeedDraft> = {},
): MonitoringFeedDraft {
	return {
		source: REGULATORY_FEED_OPTIONS[0]?.value ?? 'fda_email',
		frequency: REGULATORY_FEED_FREQUENCY_OPTIONS[0]?.value ?? 'weekly',
		detail: '',
		...overrides,
	}
}

export function sanitizeMonitoringFeedDraft(
	draft: MonitoringFeedDraft,
): MonitoringFeedDraft {
	const source = isMonitoringFeedSourceValue(draft.source)
		? draft.source
		: (REGULATORY_FEED_OPTIONS[0]?.value ?? 'fda_email')
	const frequency = isMonitoringFeedFrequencyValue(draft.frequency)
		? draft.frequency
		: (REGULATORY_FEED_FREQUENCY_OPTIONS[0]?.value ?? 'weekly')
	return {
		source,
		frequency,
		detail: draft.detail.trim(),
	}
}

export function collectMonitoringFeedErrors(
	draft: MonitoringFeedDraft,
): string[] {
	const sanitized = sanitizeMonitoringFeedDraft(draft)
	const errors: string[] = []
	if (sanitized.detail.length > 160) {
		errors.push('Keep the description under 160 characters.')
	}
	return errors
}

export function monitoringFeedCardTitle(draft: MonitoringFeedDraft): string {
	const sanitized = sanitizeMonitoringFeedDraft(draft)
	return resolveMonitoringFeedSourceLabel(sanitized.source)
}

export function formatMonitoringFeedSummary(
	draft: MonitoringFeedDraft,
): string {
	const sanitized = sanitizeMonitoringFeedDraft(draft)
	const frequencyLabel = resolveMonitoringFeedFrequencyLabel(
		sanitized.frequency,
	)
	const detail = sanitized.detail || MONITORING_FEED_COPY.summary.detailMissing
	return `${frequencyLabel} — ${detail}`
}

export function resolveMonitoringFeedSourceLabel(
	value: MonitoringFeedSourceValue,
): string {
	return (
		REGULATORY_FEED_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function resolveMonitoringFeedFrequencyLabel(
	value: MonitoringFeedFrequencyValue,
): string {
	return (
		REGULATORY_FEED_FREQUENCY_OPTIONS.find((option) => option.value === value)
			?.label ?? value
	)
}

export function isMonitoringFeedSourceValue(
	value: unknown,
): value is MonitoringFeedSourceValue {
	return REGULATORY_FEED_OPTIONS.some((option) => option.value === value)
}

export function isMonitoringFeedFrequencyValue(
	value: unknown,
): value is MonitoringFeedFrequencyValue {
	return REGULATORY_FEED_FREQUENCY_OPTIONS.some(
		(option) => option.value === value,
	)
}

export function monitoringFeedDraftToEntry(
	draft: MonitoringFeedDraft,
): MonitoringFeedEntry {
	const sanitized = sanitizeMonitoringFeedDraft(draft)
	return {
		source: sanitized.source as MonitoringFeedEntry['source'],
		frequency: sanitized.frequency as MonitoringFeedEntry['frequency'],
		detail: sanitized.detail,
	}
}

export function monitoringFeedEntryToDraft(
	entry: MonitoringFeedEntry,
): MonitoringFeedDraft {
	const source = isMonitoringFeedSourceValue(entry.source)
		? entry.source
		: undefined
	const frequency = isMonitoringFeedFrequencyValue(entry.frequency)
		? entry.frequency
		: undefined
	return sanitizeMonitoringFeedDraft(
		createMonitoringFeedDraft({
			source,
			frequency,
			detail: entry.detail,
		}),
	)
}
